import { useRef, useState, useEffect } from "react"
import styles from '../../styles/CanvasGrid.module.css';
import Link from "next/link";

// Full-screen / exit icons, drawn in currentColor so they follow the light and dark theme (theme-pink.css)
const EXPAND_PATH = "M4 9 V4 H9 M15 4 H20 V9 M20 15 V20 H15 M9 20 H4 V15";
const SHRINK_PATH = "M9 4 V9 H4 M20 9 H15 V4 M15 20 V15 H20 M4 15 H9 V20";

export default function CanvasGrid(props) {
  // Fullscreen is requested on this container (iframe + bottom bar), not the
  // iframe alone, so the exit/shrink button stays part of the fullscreen
  // element instead of disappearing once fullscreen engages.
  const canvasContainer = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [usesFallback, setUsesFallback] = useState(false);
  const [barHidden, setBarHidden] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Reset the bar to visible whenever fullscreen is (re)entered, and give
  // first-time users a brief nudge toward the handle.
  useEffect(() => {
    if (isFullscreen) {
      setBarHidden(false);
      setShowHint(true);
      const hintTimer = setTimeout(() => setShowHint(false), 1800);
      return () => clearTimeout(hintTimer);
    }
    setBarHidden(false);
  }, [isFullscreen]);

  function toggleBar() {
    setBarHidden((prev) => !prev);
    setShowHint(false);
  }

  useEffect(() => {
    const el = canvasContainer.current;
    if (
      el &&
      !(el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen)
    ) {
      // iOS Safari has no Fullscreen API for arbitrary elements; fall back
      // to a CSS-only fixed-viewport overlay instead.
      setUsesFallback(true);
    }

    const handleFullscreenChange = () => {
      const fsElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(fsElement === canvasContainer.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  function fullScreen() {
    const el = canvasContainer.current;
    if (!el) return;

    if (usesFallback) {
      setIsFullscreen((prev) => !prev);
      return;
    }

    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fsElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    }
  }

  function Sandbox(value){
    var sand_value;
    if (value == "cutedressup" || value == "playcutegames"|| value == "Y8"|| value == "VI"|| value == "bestgamespot") {
      // sand_value = "allow-scripts allow-popups allow-same-origin";
    } else {
      // sand_value =
        // "allow-forms allow-pointer-lock allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-top-navigation-by-user-activation";
    }
    return sand_value;
  }

  return (
    <div className={styles.thumb_canvas_Grid}>
      <div
        ref={canvasContainer}
        className={`canvas-containerr${usesFallback && isFullscreen ? ' fake-fullscreen' : ''}`}
      >
        <iframe
          // sandbox={Sandbox(props.brand)}
          className="game-canvas"
          title={props.name}
          height="600px"
          width="100%"
          frameBorder="0"
          scrolling="no"
          src={props.iframe}
          data-hj-allow-iframe="true"

        ></iframe>
        <div
          className={`${styles.game_info} game-info-bar${barHidden ? ' bar-hidden' : ''}${showHint ? ' hint' : ''}`}
        >
          {/* the logo only shows in full screen (theme-pink.css) */}
          <img className="game-bar-logo" src="/assets/logo/logo.png" alt="" width="89" height="47" loading="lazy" />
          <p className="game-title">{props.name}</p>
          {isFullscreen && (
            <button type="button" className="game-bar-btn" aria-label="Hide game bar" title="Hide bar" onClick={toggleBar}>
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          )}
          <a
            className="game-fullscreen"
            role="button"
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            title={isFullscreen ? "Exit full screen" : "Full screen"}
            onClick={fullScreen}
          >
            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d={isFullscreen ? SHRINK_PATH : EXPAND_PATH} />
            </svg>
          </a>
        </div>
        {/* while the bar is hidden, a small tab at the bottom brings it back */}
        {isFullscreen && barHidden && (
          <button
            type="button"
            className="game-bar-handle"
            aria-label="Show game bar"
            onClick={toggleBar}
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 15 12 9 18 15"></polyline>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
